// Approximate how an email client will display a message: rewrite the HTML the way the client
// would (drop the CSS it ignores, apply its sanitizer and conditional-comment rules), then let
// a real browser lay out the result. Pure string work, so it runs in a Worker and in the page.
//
// What each client ignores comes from caniemail.com (support.json, rebuilt by
// `pnpm email-clients:sync`). Behaviour caniemail only describes in prose lives in QUIRKS.
//
// The Outlook quirks were measured against Microsoft Word, the engine classic Outlook renders
// with: `pnpm email-clients:compare` renders each fixture in both and reports the difference.
//
// ponytail: this imitates a layout engine with CSS, it is not one. It cannot see image files
// (so no DPI-based sizing) or Windows display scaling, and Word for Mac stands in for Outlook
// for Windows. Upgrade path is rendering in the real client.

import support from './support.json'

export type EmailClientId = keyof typeof support.clients

export const EMAIL_CLIENTS: Record<EmailClientId, string> = {
  gmail: 'Gmail',
  outlook: 'Outlook for Windows (classic)',
  'outlook-com': 'Outlook.com / new Outlook',
  yahoo: 'Yahoo / AOL',
}

type Decl = [prop: string, value: string]

interface Quirks {
  /** Client default styles, added ahead of the author's. */
  baseCss?: string
  /**
   * Rewrite one declaration, or return null to drop it. `tag` is '' inside a <style> block,
   * `block` is the lowercased declaration block it came from.
   */
  decl?: (prop: string, value: string, tag: string, block: string) => Decl | null
  /** Rules that depend on the element, added last with !important so they also beat <style> rules. */
  lateCss?: string
  /** Extra declarations for an element, worked out from its whole inline style. */
  after?: (tag: string, block: string) => string
  /** Runs before the CSS filter. */
  pre?: (html: string, notes: string[]) => string
  /** Runs after the CSS filter, so the markup it emits is not filtered. */
  post?: (html: string) => string
}

const attr = (attrs: string, name: string) => new RegExp(`\\b${name}\\s*=\\s*["']([^"']*)["']`, 'i').exec(attrs)?.[1]
const px = (style: string, prop: string) => Number(new RegExp(`(?:^|;)\\s*${prop}\\s*:\\s*([\\d.]+)px`, 'i').exec(style)?.[1])

/** Word evaluates conditional comments as Office 16. */
function evalMso(expr: string): boolean {
  return expr.split('|').some(or => or.split('&').every((raw) => {
    let term = raw.replace(/[()]/g, '').trim().toLowerCase()
    const negate = term.startsWith('!')
    if (negate) term = term.slice(1).trim()
    const m = /^(?:(gte|gt|lte|lt)\s+)?mso(?:\s+(\d+))?$/.exec(term)
    let result = false
    if (m) {
      const v = Number(m[2]), op = m[1]
      result = !m[2] || (op === 'gte' ? v <= 16 : op === 'gt' ? v < 16 : op === 'lte' ? v >= 16 : op === 'lt' ? v > 16 : v === 16)
    }
    return negate ? !result : result
  }))
}

// ponytail: VML is drawn as the nearest HTML box (buttons and background rects, the two shapes
// email uses). Other shapes lose their outline and keep their text.
function vmlToHtml(html: string): string {
  return html
    .replace(/<xml>[\s\S]*?<\/xml>/gi, '')
    .replace(/<v:roundrect\b([^>]*)>([\s\S]*?)<\/v:roundrect>/gi, (_m, attrs: string, inner: string) => {
      const style = attr(attrs, 'style') ?? ''
      const arc = parseFloat(attr(attrs, 'arcsize') ?? '0') / 100
      const radius = Math.round(arc * Math.min(px(style, 'width') || 0, px(style, 'height') || 0))
      const stroke = /^(f|false)$/i.test(attr(attrs, 'stroke') ?? '') ? 'none' : `1px solid ${attr(attrs, 'strokecolor') ?? '#000'}`
      const href = attr(attrs, 'href')
      return `<a${href ? ` href="${href}"` : ''} style="display:inline-flex;align-items:center;justify-content:center;box-sizing:border-box;text-decoration:none;overflow:hidden;${style};background:${attr(attrs, 'fillcolor') ?? '#fff'};border:${stroke};border-radius:${radius}px" data-vml>${inner}</a>`
    })
    .replace(/<v:rect\b([^>]*)>([\s\S]*?)<\/v:rect>/gi, (_m, attrs: string, inner: string) => {
      let style = attr(attrs, 'style') ?? ''
      const pct = /mso-width-percent\s*:\s*(\d+)/i.exec(style)
      if (pct) style += `;width:${Number(pct[1]) / 10}%`
      const fill = /<v:fill\b([^>]*)>/i.exec(inner)?.[1] ?? ''
      const src = attr(fill, 'src')
      const image = src ? ` url('${src}') ${attr(fill, 'type') === 'tile' ? 'repeat' : 'center/cover no-repeat'}` : ''
      return `<div data-vml style="${style};background:${attr(fill, 'color') ?? attr(attrs, 'fillcolor') ?? 'transparent'}${image}">${inner}</div>`
    })
    .replace(/<v:textbox\b[^>]*>/gi, '<div>').replace(/<\/v:textbox>/gi, '</div>')
    .replace(/<\/?[vow]:[^>]*>/gi, '')
}

/**
 * Runs of line breaks, measured in Word (fixtures/breaks.html). An empty line that ends a
 * paragraph, because a block or the end of the cell follows, is only as tall as the text,
 * whatever the line-height. And breaks at the very end of a block disappear: the last three
 * after text or an image, the last two after a table.
 */
function wordBreaks(html: string): string {
  const br = '(?:<span\\b[^>]*>\\s*)?<br\\s*\\/?>(?:\\s*<\\/span>)?\\s*'
  const line = (height: string) => `<div data-vml style="height:${height}"></div>`
  const short = line('calc(1em + 1px)')
  const re = new RegExp(`(<\\/(?:table|ul|ol|p|div|h[1-6]|blockquote)>\\s*)?((?:${br})+)(<\\/(?:td|th|p|div|li|h[1-6])>)?(?=(<(?:table|div|p|ul|ol|h[1-6]|center|blockquote|hr)\\b)?)`, 'gi')
  return html.replace(re, (_m, before: string | undefined, run: string, close: string | undefined, block: string | undefined) => {
    const n = run.match(/<br/gi)?.length ?? 0
    let out = run
    if (close && /^<\/table/i.test(before ?? '')) out = line('1lh').repeat(Math.max(0, n - 2))
    else if (close) {
      // After text the first break only ends its line. After a block there is no line to end.
      const first = before ? '' : '<br>'
      out = n === 2 ? first + short : first + line('1lh').repeat(Math.max(0, n - 3))
    }
    else if (block && n > 1) out = '<br>'.repeat(n - 1) + short
    return (before ?? '') + out + (close ?? '')
  })
}

/** Wrap every run of body text in a span, so lateCss can move text without moving boxes. */
function wrapText(html: string): string {
  const body = Math.max(html.search(/<body\b/i), 0)
  const parts = html.slice(body).split(/(<!--[\s\S]*?-->|<(?:style|script|title|textarea|select)\b[\s\S]*?<\/(?:style|script|title|textarea|select)>|<(?:"[^"<]*"|'[^'<]*'|[^>"'])+>|<[^>]+>)/i)
  return html.slice(0, body) + parts.map((part, i) => i % 2 || !/\S/.test(part) ? part : `<span data-t>${part}</span>`).join('')
}

/**
 * Word sizes an image from its width/height attributes and ignores CSS sizes entirely.
 * A percentage attribute counts as no attribute: the image renders at its natural size, and
 * a table too narrow for it widens to fit, as it does in a browser.
 */
function attrSizedImages(html: string): string {
  const dim = (tag: string, name: string) => {
    const v = new RegExp(`\\s${name}\\s*=\\s*["']?(\\d+%?)`, 'i').exec(tag)?.[1]
    return v && !v.endsWith('%') ? `${v}px` : 'auto'
  }
  return html.replace(/<img\b(?:"[^"<]*"|'[^'<]*'|[^>"'])*>|<img\b[^>]*>/gi, (tag) => {
    const size = `width:${dim(tag, 'width')}!important;height:${dim(tag, 'height')}!important`
    return /\sstyle\s*=\s*"/i.test(tag) ? tag.replace(/(\sstyle\s*=\s*")([^"]*)/i, `$1$2;${size}`) : tag.replace(/^<img/i, `<img style="${size}"`)
  })
}

const BORDERED = ':is(p,div)[style*="border:"]'
const SHADED = ':is(p,h1,h2,h3,h4,h5,h6)[style*="background"]'
// Blocks whose lines are their own, not a child block's.
const BLOCKS = 'p,div,td,th,li,h1,h2,h3,h4,h5,h6'
const LEAF = `:is(${BLOCKS}):not(:has(>:is(${BLOCKS},table,ul,ol,blockquote,center)))`

// Word's single line spacing as a multiple of the font size, measured in Word per font.
// Fonts not listed get Arial's.
const SINGLE_SPACING: Record<string, number> = {
  'arial': 1.15, 'helvetica': 1.15, 'times new roman': 1.15, 'times': 1.15, 'georgia': 1.136, 'verdana': 1.215,
  'tahoma': 1.207, 'trebuchet ms': 1.161, 'courier new': 1.133, 'calibri': 1.22, 'cambria': 1.172, 'segoe ui': 1.33,
}
// --single holds that multiple. It is set beside every font-family and inherits with it.
const SINGLE = 'var(--single,1.15)'
const SINGLE_EM = `calc(${SINGLE}*1em)`
// One line of text, which is never less than about 2px tall even at font-size:0.
const OVERLAP = `calc(-1*max(1.3pt,1em)*${SINGLE})`
// How far a line's text sits from where a browser puts it: half the extra leading, down for a
// length (--lh-dir 1) and up for a multiple (-1).
const SHIFT = `calc(var(--lh-dir,0)*max(0px,1lh - ${SINGLE_EM})/2)`
// Word's sizes for the CSS font-size keywords: the old HTML 1 to 7 scale, a step up from CSS.
const FONT_SIZES: Record<string, string> = {
  'xx-small': '7.5pt', 'x-small': '10pt', 'small': '12pt', 'medium': '13.5pt', 'large': '18pt', 'x-large': '24pt', 'xx-large': '36pt',
}
const singleFor = (families: string) => SINGLE_SPACING[families.split(',')[0]!.replace(/["']|!important/gi, '').trim().toLowerCase()] ?? 1.15

/**
 * Word's reading of line-height, measured against Word itself (fixtures/lineheight.html):
 * a length is a minimum ("at least") unless mso-line-height-rule:exactly is set, a percentage
 * scales single spacing instead of the font size, and a bare number is truncated to whole lines.
 * Returned as the --lh custom property that the base stylesheet turns into line-height, so the
 * multiple is re-applied against each descendant's own font.
 */
function wordLineHeight(v: string, exactly: boolean): Decl {
  const n = parseFloat(v)
  if (Number.isNaN(n)) return ['--lh', 'initial;--lh-dir:0;--lh0:0']
  // Zero is honoured literally, and marks the block so image lines can overlap (see lateCss).
  if (n === 0) return ['--lh', '0px;--lh-dir:0;--lh0:1']
  if (/^[\d.]+$/.test(v)) return ['--lh', `calc(${Math.max(1, Math.floor(n))}*${SINGLE});--lh-dir:-1;--lh0:0`]
  if (v.endsWith('%')) return ['--lh', `calc(${n / 100}*${SINGLE});--lh-dir:-1;--lh0:0`]
  return ['--lh', `${exactly ? v : `max(${v},${SINGLE_EM})`};--lh-dir:1;--lh0:0`]
}

const NAMED: Record<string, string> = {
  black: '000000', white: 'ffffff', red: 'ff0000', green: '008000', blue: '0000ff', navy: '000080', gray: '808080', grey: '808080',
  silver: 'c0c0c0', maroon: '800000', purple: '800080', teal: '008080', olive: '808000', yellow: 'ffff00', orange: 'ffa500',
  fuchsia: 'ff00ff', lime: '00ff00', aqua: '00ffff',
}

/**
 * Word's "automatic" text colour, which text takes when nothing sets one: white on a dark
 * background, black otherwise. The cut-off was measured in Word (fixtures/autocolor.html).
 * Null when the colour is not one we can read.
 */
function autoText(background: string): string | null {
  const c = background.trim().toLowerCase()
  const hex = /^#([0-9a-f]{3}|[0-9a-f]{6})$/.exec(c)?.[1] ?? NAMED[c]
  const rgb = hex
    ? (hex.length === 3 ? [...hex].map(d => parseInt(d + d, 16)) : [0, 2, 4].map(i => parseInt(hex.slice(i, i + 2), 16)))
    : /^rgb\(\s*(\d+)[\s,]+(\d+)[\s,]+(\d+)\s*\)$/.exec(c)?.slice(1).map(Number)
  if (!rgb) return null
  return 0.299 * rgb[0]! + 0.587 * rgb[1]! + 0.114 * rgb[2]! < 74 ? '#fff' : '#000'
}

/** Give every element with a bgcolor attribute the automatic text colour that goes with it. */
function bgcolorText(html: string): string {
  return html.replace(/<(?:table|tr|td|th|body)\b(?:"[^"<]*"|'[^'<]*'|[^>"'])*>/gi, (tag) => {
    const auto = autoText(/\sbgcolor\s*=\s*["']?([^"'\s>]+)/i.exec(tag)?.[1] ?? '')
    // A CSS background on the same element wins, and has set --auto already.
    if (!auto || /\sstyle\s*=\s*"[^"]*--auto:/i.test(tag)) return tag
    return /\sstyle\s*=\s*"/i.test(tag) ? tag.replace(/(\sstyle\s*=\s*")/i, `$1--auto:${auto};`) : tag.replace(/^<(\w+)/, `<$1 style="--auto:${auto}"`)
  })
}

const SIDES = ['top', 'right', 'bottom', 'left'] as const
const px4 = (v: string | undefined) => (v?.match(/[\d.]+(?=px)|\b0\b/g) ?? []).map(Number)

/** Per-side pixel sizes for `border` (widths, capped like Word) or `padding` in an inline style. */
function sides(block: string, prop: 'border' | 'padding'): Record<typeof SIDES[number], number> {
  const get = (name: string) => new RegExp(`(?:^|;)\\s*${name}\\s*:\\s*([^;]+)`).exec(block)?.[1]
  const shorthand = px4(prop === 'border' ? (get('border-width') ?? get('border')) : get('padding'))
  const out = { top: 0, right: 0, bottom: 0, left: 0 }
  SIDES.forEach((side, i) => {
    const all = shorthand.length ? shorthand[[0, shorthand.length > 1 ? 1 : 0, shorthand.length > 2 ? 2 : 0, shorthand.length > 3 ? 3 : shorthand.length > 1 ? 1 : 0][i]!]! : 0
    const own = px4(get(`${prop}-${side}`) ?? (prop === 'border' ? get(`border-${side}-width`) : undefined))[0]
    out[side] = Math.min(own ?? all, prop === 'border' ? 8 : 41)
  })
  return out
}

/** The top or bottom margin a declaration block sets, or null when it leaves Word's default. */
function blockMargin(block: string, side: 'top' | 'bottom'): string | null {
  const longhand = new RegExp(`margin-${side}\\s*:\\s*([^;!]+)`).exec(block)?.[1]
  const shorthand = /(?:^|;)\s*margin\s*:\s*([^;!]+)/.exec(block)?.[1]?.trim().split(/\s+/)
  const v = (longhand ?? (shorthand && (side === 'top' ? shorthand[0] : shorthand[2] ?? shorthand[0])))?.trim()
  return v && /^[\d.]+(px|pt|em)?$/.test(v) ? (/[a-z]$/.test(v) ? v : `${v}px`) : null
}

/**
 * Word draws <hr> as one line of the surrounding text with the rule sitting on the
 * baseline, and takes size, colour, width and alignment from attributes only. Of its CSS only
 * the margins count, so rebuild its style from those.
 */
function wordRules(html: string): string {
  return html.replace(/<hr\b(?:"[^"<]*"|'[^'<]*'|[^>"'])*>|<hr\b[^>]*>/gi, (tag) => {
    const get = (name: string) => new RegExp(`\\s${name}\\s*=\\s*["']?([^"'\\s>]+)`, 'i').exec(tag)?.[1]
    const thick = Number(get('size')) || 1, colour = get('color') ?? '#e0e0e0', width = get('width') ?? '100%'
    const x = { left: '0', right: '100%' }[get('align')?.toLowerCase() ?? ''] ?? '50%'
    const margins = (/\sstyle\s*=\s*"([^"]*)"/i.exec(tag)?.[1] ?? '').split(';').filter(d => /^\s*margin(-top|-bottom)?\s*:/i.test(d)).join(';')
    const style = `margin:0;${margins};border:0!important;height:calc(${SINGLE_EM} + ${thick - 1}px)!important;background:linear-gradient(${colour},${colour}) ${x} calc(${SINGLE_EM}*.76 - ${thick}px)/${/%$/.test(width) ? width : `${width}px`} ${thick}px no-repeat!important`
    return `<hr style="${style}">`
  })
}

/**
 * Word hides the text of a display:none (or mso-hide:all) block but still draws tables nested
 * in it. Swap display:none for a marker (see lateCss) on blocks that contain a table.
 */
function leakNestedTables(html: string): string {
  return html.replace(/<(div|p|span|td|th|a|center|li|h[1-6])\b[^>]*\sstyle="[^"]*display:\s*none[^"]*"[^>]*>/gi, (tag: string, name: string, at: number) => {
    const edges = new RegExp(`<(/?)${name}\\b`, 'gi')
    edges.lastIndex = at + tag.length
    let depth = 1, end = html.length
    for (let m = edges.exec(html); m; m = edges.exec(html)) {
      depth += m[1] ? -1 : 1
      if (!depth) { end = m.index; break }
    }
    return /<table\b/i.test(html.slice(at, end)) ? tag.replace(/display:\s*none(\s*!important)?/gi, '--mso-hidden:1$1') : tag
  })
}

const QUIRKS: Record<EmailClientId, Quirks> = {
  gmail: {
    baseCss: 'body{font:small/1.5 Arial,Helvetica,sans-serif}a{color:#15c}',
    pre(html, notes) {
      const bytes = new TextEncoder().encode(html).length
      if (bytes > 102 * 1024) notes.push(`Gmail clips messages over 102 KB. This one is ${Math.round(bytes / 1024)} KB.`)
      const body = html.search(/<body\b/i)
      return html.replace(/<style\b[^>]*>([\s\S]*?)<\/style>/gi, (m, css: string, at: number) => {
        if (body >= 0 && at > body) return notes.push('Gmail removed a <style> block inside <body>.'), ''
        if (css.length > 16 * 1024) return notes.push('Gmail removed a <style> block over 16 KB.'), ''
        return m
      })
    },
  },
  outlook: {
    // Word spaces blocks by a fixed 14pt, where browsers use 1em of the block's own font size,
    // and drops that default spacing at the start and end of the body and of every table cell.
    // :where() keeps these at zero specificity, so any margin the author sets still wins.
    // The :first-child/:last-child must sit inside it, or they add specificity of their own.
    // Word sets text without kerning or ligatures, which decides where long lines wrap.
    baseCss: `body{font-family:'Times New Roman',serif;font-kerning:none;font-variant-ligatures:none}`
      // Every line-height goes through --lh (see wordLineHeight). Without one, single spacing.
      + `:where(*){line-height:var(--lh,${SINGLE})}`
      // Text colour goes through --c the same way. With none set, text is "automatic":
      // --auto, which every background sets to white or black (see autoText).
      // Wrapped text ([data-t]) simply inherits. A link is blue unless something says otherwise.
      + ':where(*:not(a[href],font[color],body[text],[data-t])){color:var(--c,var(--auto,#000))}'
      + ':where(a[href]){--c:#00f;color:#00f}'
      + ':where(p,h1,h2,h3,h4,h5,h6,ul,ol){margin-top:14pt;margin-bottom:14pt}'
      + ':where(:is(body,td,th)>:is(p,h1,h2,h3,h4,h5,h6,ul,ol):first-child){margin-top:0}'
      + ':where(:is(body,td,th)>:is(p,h1,h2,h3,h4,h5,h6):last-child){margin-bottom:0}'
      // Superscripts and subscripts do not make their line taller.
      + ':where(sup,sub){line-height:0}'
      // Paragraphs inside a list item bring no spacing of their own.
      + ':where(li>:is(p,h1,h2,h3,h4,h5,h6)){margin-top:0;margin-bottom:0}'
      // Tables floated with align sit 4.5pt apart unless mso-table-lspace/rspace says otherwise,
      // which is what makes two columns that exactly fill their row stack in Outlook.
      + ':where(table[align=left]+table[align=left]){margin-left:4.5pt}'
      + ':where(table[align=right]+table[align=right]){margin-right:4.5pt}'
      // A border attribute draws thin light grey lines, not the browser's dark bevel.
      + ':where(table[border]:not([border="0"]),table[border]:not([border="0"]) :is(td,th)){border-color:#dcdcdc;border-style:solid}'
      + ':where(ul,ol){padding-left:36pt}'
      // Text styles do not inherit into a table: its cells start again from Word's defaults.
      + `:where(table){font:12pt 'Times New Roman',serif;--c:initial;--lh:initial;--lh-dir:0;--lh0:0;--single:1.15;--ps:0;--tmt:0px;--tmb:0px;--tp:0;--tp-top:0px;--tp-right:0px;--tp-bottom:0px;--tp-left:0px}`
      + ':where(blockquote){margin:5pt 36pt}:where(pre){margin:0}'
      // --mb/--mt: the margins Word paints a paragraph's background across (see decl).
      + ':where(p,h1,h2,h3,h4,h5,h6){--mb:14pt;--mt:14pt}'
      + ':where(:is(body,td,th)>:is(p,h1,h2,h3,h4,h5,h6):last-child){--mb:0px}'
      + ':where(:is(body,td,th)>:is(p,h1,h2,h3,h4,h5,h6):first-child){--mt:0px}'
      // Headings have fixed sizes, where a browser scales them from the surrounding text.
      + ':where(h1){font-size:24pt}:where(h2){font-size:18pt}:where(h3){font-size:13.5pt}:where(h4){font-size:12pt}:where(h5){font-size:10pt}:where(h6){font-size:7.5pt}',
    // Word: padding only on table cells, no CSS size on text boxes, max-width only on tables.
    lateCss: ':not(td,th,ul,ol,[data-vml]){padding:0!important}'
      + 'div:not([data-vml]),p,span,body{width:auto!important;height:auto!important}'
      + ':not(table,[data-vml]){max-width:none!important}'
      + 'span,body{margin:0!important}'
      + 'table{height:auto!important}'
      // The background attribute's image is not drawn on tables or cells.
      + ':is(table,td,th)[background]{background-image:none!important}'
      // A line holding only an image is as tall as the image, with no gap for descenders.
      + 'img{vertical-align:bottom}'
      // With line-height:0 a line that holds an image and has another line after it is one
      // line of text shorter than the image, so the next line overlaps it. Even at font-size:0
      // Word keeps about 2px of that (measured: stacked slices each lose 2px), hence the 1.3pt
      // floor in OVERLAP.
      + `@container style(--lh0:1){:is(img:has(+:is(br,img,a)),a:has(+:is(br,img,a))>img){margin-bottom:${OVERLAP}!important}`
      // The first line of the block gives up the same amount again at its top.
      + `:is(img,a:has(>img)):first-child:has(+:is(br,img,a)){margin-top:${OVERLAP}!important}}`
      // vspace is ignored, and hspace only keeps text away on the side the text is on.
      + 'img:is([hspace],[vspace]){margin-top:0!important;margin-bottom:0!important}'
      + 'img[align=right][hspace]{margin-right:0!important}img[align=left][hspace]{margin-left:0!important}'
      // Browsers split a tall line's extra space above and below the text. Word puts all of
      // it above for a length and all of it below for a multiple. Every run of text is wrapped
      // in a span (see wrapText) and moved by the half it is off, without changing the layout.
      + `[data-t]{position:relative;top:${SHIFT};line-height:inherit}`
      // Padding on a table (--tp-*) is the default padding of its cells.
      + '@container style(--tp:1){:where(td,th){padding:var(--tp-top) var(--tp-right) var(--tp-bottom) var(--tp-left)}}'
      // A table's vertical margin (--tmt/--tmb) goes to the paragraphs in every cell instead
      // of to the table. --ps switches these pseudo-elements on only inside such a table,
      // because a block ::before takes text-indent away from the text.
      + '@container style(--ps:1){'
      + `${LEAF}::before{content:"";display:block;height:var(--tmt,0px)}`
      + `${LEAF}::after{content:"";display:block;height:var(--tmb,0px)}`
      + '}'
      + 'a:not([href]){color:inherit!important;text-decoration:none!important}'
      // Superscript and subscript are a fixed text effect: their font-size cannot be changed.
      + 'sup,sub{font-size:.65em!important}'
      // An empty paragraph or heading takes no room at all, margins included.
      + ':is(p,h1,h2,h3,h4,h5,h6):empty{display:none!important}'
      // Word ignores margins set on a list: it keeps its own 14pt, and none on a nested list.
      + 'ul,ol{margin-top:14pt!important;margin-bottom:14pt!important}'
      + ':is(body,td,th)>:is(ul,ol):first-child{margin-top:0!important}'
      + 'li>:is(ul,ol){margin-top:0!important;margin-bottom:0!important}'
      // A list item is a paragraph to Word, so an end item's own paragraph margin replaces
      // the list's.
      + ':is(ul,ol):has(>li:first-child>p:first-child[style*="margin"]){margin-top:0!important}'
      + ':is(ul,ol):has(>li:last-child>p:last-child[style*="margin"]){margin-bottom:0!important}'
      // A shaded paragraph's background covers the margin towards a shaded neighbour (see decl).
      + `${SHADED}:has(+${SHADED}){--sb:var(--mbv)}${SHADED}+${SHADED}{--st:var(--mtv)}`
      // Neighbouring paragraphs with borders share one box.
      + `${BORDERED}:has(+${BORDERED}){border-bottom-width:0!important;padding-bottom:0!important}`
      + `${BORDERED}+${BORDERED}{border-top-width:0!important;padding-top:0!important}`
      // Hidden blocks that still show their nested tables (see leakNestedTables). A <style>
      // rule that un-hides the block resets the marker (see decl), which is why this is a
      // registered, non-inherited property and not an attribute.
      + '@property --mso-hidden{syntax:"<number>";inherits:false;initial-value:0}'
      + '[style*="--mso-hidden:1"]{font-size:calc(1em*(1 - var(--mso-hidden)))!important;line-height:calc(1.15*(1 - var(--mso-hidden)))!important}'
      + '@container style(--mso-hidden:1){:not(table){display:none!important}}',
    pre: html => html
      // The "revealed" form never shows in Word, whatever the expression: when it is true,
      // the <!--> that follows opens a comment that swallows the content.
      .replace(/<!--\[if [^\]]+\]><!-->[\s\S]*?<!--<!\[endif\]-->/gi, '')
      .replace(/<!--\[if ([^\]]+)\]>([\s\S]*?)<!\[endif\]-->/gi, (_m, expr: string, inner: string) => evalMso(expr) ? inner : ''),
    post: html => wrapText(wordBreaks(wordRules(leakNestedTables(attrSizedImages(bgcolorText(vmlToHtml(html)))))))
      // White space between two linked images takes no room in Word.
      .replace(/(<img\b[^>]*>\s*<\/a>)\s+(?=<a\b[^>]*>\s*<img\b)/gi, '$1'),
    after(tag, block) {
      if (!/border/.test(block)) return ''
      const border = sides(block, 'border')
      if (tag === 'table') {
        // Word leaves about the border's own width clear between a table's border and its cells.
        const w = Math.min(border.top, border.left)
        const gap = !w ? 0 : w <= 2 ? 1 : w
        return gap ? `padding:${gap}px!important` : ''
      }
      if (!/^(p|div|h[1-6])$/.test(tag) || !SIDES.some(side => border[side])) return ''
      // A bordered paragraph keeps its text on the column edge: the border, and the padding
      // between border and text (which only counts on bordered sides), hang outside it.
      const padding = sides(block, 'padding')
      const pad = (side: typeof SIDES[number]) => border[side] ? padding[side] : 0
      return `padding:${SIDES.map(side => `${pad(side)}px`).join(' ')}!important;margin-left:-${border.left + pad('left')}px!important;`
        + `margin-right:-${border.right + pad('right')}px!important;background-clip:content-box`
    },
    // `tag` is '' inside <style>, where lateCss covers the element rules instead.
    decl(prop, value, tag, block) {
      const v = value.replace(/!important/i, '').trim().toLowerCase()
      // In a <style> block, Word drops a declaration whose !important has no space before it.
      if (!tag && /\S!important/i.test(value)) return null
      if (prop === 'line-height') return wordLineHeight(v, /mso-line-height-rule\s*:\s*exactly/.test(block))
      // The mso-*-alt properties replace their CSS counterpart in Word.
      if (prop === 'mso-padding-alt') return /^t[dh]$/.test(tag) ? ['padding', `${v}!important`] : null
      if (prop === 'mso-line-height-alt') {
        const [name, alt] = wordLineHeight(v, /mso-line-height-rule\s*:\s*exactly/.test(block))
        return [name, `${alt.replace(/;/g, '!important;')}!important`]
      }
      if (/^mso-margin-(top|bottom)-alt$/.test(prop)) {
        return /^(span|body)$/.test(tag) ? null : [prop.slice(4, -4), `${v === 'auto' ? '14pt' : v}!important`]
      }
      if (/^mso-table-[lr]space$/.test(prop)) return [prop === 'mso-table-lspace' ? 'margin-left' : 'margin-right', v]
      if (prop === 'color') return [prop, `${value};--c:${v}`]
      if (prop === 'font-size' && FONT_SIZES[v]) return [prop, FONT_SIZES[v]]
      // text-align also positions the tables inside the block, as the align attribute does.
      if (prop === 'text-align' && /^(center|right)$/.test(v)) return [prop, `-webkit-${v}`]
      // No alpha in hex colours: the declaration is ignored.
      if (/#(?:[0-9a-f]{4}|[0-9a-f]{8})\b/i.test(v)) return null
      if (tag === 'table' && /^margin(-top|-bottom)?$/.test(prop) && !/auto|(^|\s)-[\d.]/.test(v)) {
        const parts = v.split(/\s+/)
        if (prop === 'margin-top') return [prop, `0;--tmt:${v};--ps:1`]
        if (prop === 'margin-bottom') return [prop, `0;--tmb:${v};--ps:1`]
        return [prop, `0 ${parts[1] ?? parts[0]} 0 ${parts[3] ?? parts[1] ?? parts[0]};--tmt:${parts[0]};--tmb:${parts[2] ?? parts[0]};--ps:1`]
      }
      if (prop === 'font-family') return [prop, `${value};--single:${singleFor(value)}`]
      if (prop === 'font') {
        const families = /[\d.]+(?:px|pt|em|rem|%)(?:\s*\/\s*[\w.%]+)?\s+(.+)$/i.exec(value)?.[1]
        if (!families) return [prop, value]
        // The shorthand also resets line-height, to single spacing unless it carries its own.
        const reset = /\d(?:px|pt|em|rem|%)\s*\//i.test(value) ? '' : `;--lh:initial;--lh-dir:0;line-height:${SINGLE}`
        return [prop, `${value};--single:${singleFor(families)}${reset}`]
      }
      const box = /^(body|span|div|p|a)$/.test(tag)
      // An image takes no CSS margin.
      if (tag === 'img' && prop.startsWith('margin')) return null
      // A link is always inline, and a list always has its markers.
      if (tag === 'a' && prop === 'display' && v !== 'none') return null
      if (/^list-style(-type)?$/.test(prop) && /\bnone\b/.test(v)) return null
      if (prop === 'mso-hide' && v === 'all') return ['display', 'none!important']
      // Padding on a table is its cells' default padding.
      if (tag === 'table' && /^padding(-(top|right|bottom|left))?$/.test(prop)) {
        if (prop !== 'padding') return [`--tp-${prop.slice(8)}`, `${v};--tp:1`]
        const p = v.split(/\s+/)
        return ['--tp', `1;--tp-top:${p[0]};--tp-right:${p[1] ?? p[0]};--tp-bottom:${p[2] ?? p[0]};--tp-left:${p[3] ?? p[1] ?? p[0]}`]
      }
      if (tag && prop.startsWith('padding') && !/^t[dh]$/.test(tag)) return null
      if (prop === 'width' && (box || tag === 'img')) return null
      if (prop === 'height' && box) return null
      if (prop === 'max-width' && tag && tag !== 'table') return null
      if (prop.startsWith('margin') && (/^(span|body)$/.test(tag) || /auto|(^|\s)-[\d.]/.test(v))) return null
      if (prop === 'display' && (!/^(none|block|inline)$/.test(v) || (v === 'none' && tag === 'img'))) return null
      // Word's thickest border is 6pt.
      if (/^border(-(top|right|bottom|left))?(-width)?$/.test(prop)) {
        const capped = value.replace(/([\d.]+)(px|pt)/gi, (_m, n: string, unit: string) => `${Math.min(Number(n), unit.toLowerCase() === 'px' ? 8 : 6)}${unit}`)
        // A border on inline text takes up room in Word, where a browser lets it overlap.
        return [prop, /^(a|span)$/.test(tag) ? `${capped};display:inline-block` : capped]
      }
      // A <style> rule that shows a block also cancels the inline hidden marker.
      if (prop === 'display' && !tag && v !== 'none') return [prop, `${value};--mso-hidden:0${/!important/i.test(value) ? '!important' : ''}`]
      if (prop === 'background' || prop === 'background-color') {
        // Only the colour of a background survives.
        const paint = /^(?:#[0-9a-f]{3,6}\b|rgb\([^)]*\)|[a-z]+\b)/i.exec(value.replace(/url\([^)]*\)|[\w-]+-gradient\([^;]*\)/gi, '').trim())?.[0]
        if (!paint || /^(none|no-repeat|repeat|center|top|left|right|bottom|scroll|fixed)$/i.test(paint)) return null
        const auto = autoText(paint)
        const color = paint + (/!important/i.test(value) ? ' !important' : '') + (auto ? `;--auto:${auto}` : '')
        // Between two shaded paragraphs, Word paints the background across the margin as well.
        // --sb/--st are that margin where the neighbour is shaded too, else 0 (see lateCss).
        // A shadow is a copy of the box, so a margin taller than the box needs a second copy
        // half way to leave no gap.
        const mb = blockMargin(block, 'bottom') ?? 'var(--mb)', mt = blockMargin(block, 'top') ?? 'var(--mt)'
        const spill = /^(p|h[1-6])$/.test(tag) ? `;--mbv:${mb};--mtv:${mt};box-shadow:0 var(--sb,0px) 0 ${paint},0 calc(var(--sb,0px)/2) 0 ${paint},0 calc(-1*var(--st,0px)) 0 ${paint},0 calc(var(--st,0px)/-2) 0 ${paint}` : ''
        return [prop, color + spill]
      }
      return [prop, value]
    },
  },
  'outlook-com': {},
  yahoo: {
    decl: (prop, value) => [prop === 'height' ? 'min-height' : prop, value],
  },
}

interface Compiled {
  props: string[]
  values: string[]
  propValues: [prop: string, value: RegExp][]
  units: RegExp | null
  at: Set<string>
  selector: RegExp | null
  quirks: Quirks
}

const SELECTORS: Record<string, string> = {
  attribute: '\\[',
  child: '>',
  'adjacent-sibling': '\\+',
  'general-sibling': '~',
  universal: '\\*',
  chaining: '\\.[\\w-]+\\.',
  id: '#',
  class: '\\.',
}

const compiled = new Map<EmailClientId, Compiled>()
function compile(id: EmailClientId): Compiled {
  let c = compiled.get(id)
  if (c) return c
  const s = support.clients[id]
  const selectors = s.selectors.map(k => SELECTORS[k]).filter(Boolean)
  if (s.pseudo.length) selectors.push(`::?(?:${s.pseudo.join('|')})(?![\\w-])`)
  c = {
    // Yahoo's "no height" is really a rename to min-height, handled as a quirk.
    props: id === 'yahoo' ? s.props.filter(p => p !== 'height') : s.props,
    values: s.values.filter(v => !v.includes(':')),
    propValues: s.values.filter(v => v.includes(':')).map((v) => {
      const [prop, value] = v.split(':') as [string, string]
      return [prop, new RegExp(`\\b${value}\\b`)]
    }),
    units: s.units.length ? new RegExp(`[\\d.](?:${s.units.join('|')})\\b`) : null,
    at: new Set(s.at),
    selector: selectors.length ? new RegExp(selectors.join('|')) : null,
    quirks: QUIRKS[id],
  }
  compiled.set(id, c)
  return c
}

/** Split on `sep`, ignoring separators inside parentheses or quotes (data: URLs contain `;`). */
function splitTop(s: string, sep: string): string[] {
  const parts: string[] = []
  let depth = 0, quote = '', start = 0
  for (let i = 0; i < s.length; i++) {
    const ch = s[i]!
    if (quote) { if (ch === quote) quote = '' }
    else if (ch === '"' || ch === '\'') quote = ch
    else if (ch === '(') depth++
    else if (ch === ')') depth--
    else if (ch === sep && depth <= 0) { parts.push(s.slice(start, i)); start = i + 1 }
  }
  parts.push(s.slice(start))
  return parts
}

function filterDecls(body: string, tag: string, c: Compiled, dropped: Set<string>): string {
  const kept: string[] = [], block = body.toLowerCase()
  for (const raw of splitTop(body, ';')) {
    const colon = raw.indexOf(':')
    if (colon < 0) continue
    const prop = raw.slice(0, colon).trim().toLowerCase(), value = raw.slice(colon + 1).trim()
    const v = value.toLowerCase()
    const unsupported = c.props.some(p => prop === p || prop.startsWith(`${p}-`))
      || c.values.some(x => v.includes(x))
      || (prop.startsWith('--') && c.values.includes('var('))
      || c.propValues.some(([p, re]) => p === prop && re.test(v))
      || !!c.units?.test(v)
    const decl = unsupported ? null : c.quirks.decl ? c.quirks.decl(prop, value, tag, block) : [prop, value]
    if (decl) kept.push(`${decl[0]}:${decl[1]}`)
    if (!decl) dropped.add(prop)
  }
  const extra = tag && c.quirks.after ? c.quirks.after(tag, block) : ''
  if (extra) kept.push(extra)
  return kept.join(';')
}

function filterCss(input: string, c: Compiled, dropped: Set<string>): string {
  const css = input.replace(/\/\*[\s\S]*?\*\//g, '')
  let out = '', i = 0
  while (i < css.length) {
    const open = css.indexOf('{', i), semi = css.indexOf(';', i)
    // Statement at-rule (@import, @charset).
    if (semi >= 0 && (open < 0 || semi < open) && css.slice(i, semi).trim().startsWith('@')) {
      const stmt = css.slice(i, semi + 1).trim(), name = /^@([\w-]+)/.exec(stmt)?.[1] ?? ''
      if (c.at.has(name)) dropped.add(`@${name}`)
      else out += stmt
      i = semi + 1
      continue
    }
    if (open < 0) break
    let depth = 1, end = open + 1
    while (end < css.length && depth) {
      if (css[end] === '{') depth++
      else if (css[end] === '}') depth--
      end++
    }
    const prelude = css.slice(i, open).trim(), body = css.slice(open + 1, depth ? end : end - 1)
    i = end
    if (prelude.startsWith('@')) {
      const name = /^@([\w-]+)/.exec(prelude)?.[1] ?? ''
      if (c.at.has(name)) dropped.add(`@${name}`)
      else out += `${prelude}{${/^(media|supports)$/.test(name) ? filterCss(body, c, dropped) : body}}`
      continue
    }
    const selectors = splitTop(prelude, ',').filter((sel) => {
      const bad = c.selector?.exec(sel)
      if (bad) dropped.add(`${bad[0]} selectors`)
      return !bad
    })
    const decls = filterDecls(body, '', c, dropped)
    if (selectors.length && decls) out += `${selectors.join(',')}{${decls}}`
  }
  return out
}

/** Rewrite `html` the way `client` would. `notes` says what the client ignored or removed. */
export function emulateClient(html: string, client: EmailClientId): { html: string, notes: string[] } {
  const c = compile(client), notes: string[] = [], dropped = new Set<string>()
  let out = c.quirks.pre ? c.quirks.pre(html, notes) : html

  out = out.replace(/(<style\b[^>]*>)([\s\S]*?)(<\/style>)/gi, (_m, start: string, css: string, end: string) => start + filterCss(css, c, dropped) + end)
  // Quoted attribute values may contain `>` (alt="Shop now >"). ponytail: a tag with an
  // unbalanced quote ahead of style="" is left unfiltered. Swap in a real tokenizer if that
  // shows up.
  out = out.replace(/<([a-zA-Z][\w:-]*)\b((?:"[^"<]*"|'[^'<]*'|[^>"'])*?\s)style\s*=\s*(?:"([^"]*)"|'([^']*)')/gi, (_m, tag: string, before: string, dq?: string, sq?: string) =>
    `<${tag}${before}style="${filterDecls(dq ?? sq ?? '', tag.toLowerCase(), c, dropped).replace(/"/g, '&quot;')}"`)

  if (c.quirks.post) out = c.quirks.post(out)
  if (c.quirks.baseCss) {
    const base = `<style>${c.quirks.baseCss}</style>`
    // Never ahead of the doctype: that would put the preview in quirks mode.
    out = /<head\b[^>]*>/i.test(out) ? out.replace(/<head\b[^>]*>/i, m => m + base)
      : /<body\b/i.test(out) ? out.replace(/<body\b/i, m => base + m)
        : base + out
  }
  if (c.quirks.lateCss) {
    const late = `<style>${c.quirks.lateCss}</style>`
    out = /<\/body>/i.test(out) ? out.replace(/<\/body>/i, m => late + m) : out + late
  }
  if (dropped.size) notes.push(`${EMAIL_CLIENTS[client]} ignores: ${[...dropped].sort().join(', ')}`)
  return { html: out, notes }
}
